import { COMPOUND_LIB } from "@/services/mock/compound-lib";
import type { FormulationEntry } from "@/utils/types/compound";
import type {
  CompareCatalog,
  CompareDescriptor,
  CompareFormula,
} from "@/utils/types/compare";
import type { Variant } from "@/utils/types/variant";
import type {
  SimilarSuggestion,
  AiSuggestion,
} from "@/services/compare_api";

const compoundById = (id: string) => COMPOUND_LIB.find((c) => c.id === id);

// Top 1–2 substances + total compound count. The popover row pins the count
// and lets the names ellipsis when the row is narrow.
export function compareDescriptorFor(
  formulation: FormulationEntry[]
): CompareDescriptor {
  if (!formulation || formulation.length === 0) {
    return { names: [], count: 0 };
  }
  const sorted = [...formulation].sort((a, b) => b.ppm - a.ppm);
  const names = sorted
    .slice(0, 2)
    .map((f) => compoundById(f.compoundId)?.name)
    .filter((n): n is string => Boolean(n));
  return { names, count: formulation.length };
}

// Builds the 3-section catalog consumed by CompareSelectPopover.
//
// - yours    = the workbench's other variants of THIS flavor.
// - similar  = other org flavors with the same TARGET (server-provided).
// - ai       = nblock.result rows targeting the same TARGET (server).
//
// For `similar` and `ai`, formulation_version_items are out of scope for
// this iteration — server returns itemCount only, so the descriptor shows
// "N items" and `formulation` stays an empty array. Loss/Cost rendering
// in the popover falls back to "—" for these rows.
export function buildCompareCatalog(
  variants: Variant[],
  activeVariantId: string,
  similar: SimilarSuggestion[],
  ai: AiSuggestion[],
): CompareCatalog {
  const yours: CompareFormula[] = variants
    .filter((v) => v.id !== activeVariantId)
    .map((v) => ({
      id: `var-${v.id}`,
      name: v.name,
      descriptor: compareDescriptorFor(v.formulation),
      source: "yours",
      formulation: v.formulation,
    }));

  const similarFormulae: CompareFormula[] = similar.map((s) => ({
    id: s.id,
    name: s.flavorName ? `${s.flavorName} · ${s.formulaName}` : s.formulaName,
    descriptor: { names: [], count: s.itemCount },
    source: "similar",
    formulation: [],
  }));

  const aiFormulae: CompareFormula[] = ai.map((a) => ({
    id: a.id,
    name: `Formula #${a.resultId}`,
    descriptor: { names: [], count: a.itemCount },
    source: "ai",
    formulation: [],
    finalLossL2Sq: a.finalLossL2Sq,
  }));

  return { yours, similar: similarFormulae, ai: aiFormulae };
}
